import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  ChevronDown,
  ChevronUp,
  CreditCard,
  Download,
  FileText,
  Loader2,
  Percent,
  RefreshCw,
  Search,
  Sparkles,
  Users,
  X,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { classesApi, feesApi } from '../api/services.js'
import Avatar from '../components/Avatar.jsx'
import PageHeader from '../components/PageHeader.jsx'
import { isAcademicYear } from '../utils/academicYear.js'

const statusColors = {
  paid: 'bg-green-50 text-green-700 border-green-100',
  partial: 'bg-amber-50 text-amber-700 border-amber-100',
  pending: 'bg-slate-100 text-slate-600 border-slate-200',
  overdue: 'bg-red-50 text-red-600 border-red-100',
}

const studentName = (account) => {
  const student = account?.studentId
  if (!student) return '—'
  if (student.firstName || student.lastName) return `${student.firstName || ''} ${student.lastName || ''}`.trim()
  return student.name || '—'
}

const classLabel = (classItem) => `${classItem?.name || 'Class'}${classItem?.section ? ` — ${classItem.section}` : ''}`
const getClassId = (item) => item?._id || item?.id || item
const formatCurrency = (value = 0) => `₹${Number(value || 0).toLocaleString('en-IN')}`

const STATUS_ORDER = { overdue: 0, partial: 1, pending: 2, paid: 3 }

function getCombinedStatus(accounts) {
  const hasOutstanding = accounts.some((a) => Number(a?.outstanding ?? 0) > 0)
  const hasOverdue = accounts.some(
    (a) => String(a?.status || '').toLowerCase() === 'overdue' && Number(a?.outstanding ?? 0) > 0,
  )
  const hasPaid = accounts.some((a) => Number(a?.paidAmount ?? 0) > 0)
  if (hasOverdue) return 'overdue'
  if (!hasOutstanding) return 'paid'
  if (hasPaid) return 'partial'
  return 'pending'
}

function groupByStudent(rawAccounts) {
  const groups = new Map()
  rawAccounts.forEach((account) => {
    const student = account?.studentId
    const sid = student?._id || student?.id || 'unknown'
    const year = account?.academicYear || ''
    const key = `${sid}::${year || 'no-year'}`
    const g = groups.get(key) || {
      _groupId: key,
      student,
      studentName: (() => {
        if (!student) return '—'
        if (student.firstName || student.lastName) return `${student.firstName || ''} ${student.lastName || ''}`.trim()
        return student.name || '—'
      })(),
      admissionNo: student?.admissionNo || '',
      classItem: account?.classId,
      academicYear: year,
      totalAmount: 0,
      paidAmount: 0,
      outstanding: 0,
      discountAmount: 0,
      concessionAmount: 0,
      fineAmount: 0,
      accounts: [],
    }
    g.totalAmount += Number(account?.totalAmount || 0)
    g.paidAmount += Number(account?.paidAmount || 0)
    g.outstanding += Number(account?.outstanding ?? Math.max(0, (account?.totalAmount || 0) - (account?.paidAmount || 0)))
    g.discountAmount += Number(account?.discountAmount || 0)
    g.concessionAmount += Number(account?.concessionAmount || 0)
    g.fineAmount += Number(account?.fineAmount || 0)
    g.accounts.push(account)
    groups.set(key, g)
  })

  return [...groups.values()]
    .map((g) => {
      const netTotal = Math.max(0, g.totalAmount - g.discountAmount - g.concessionAmount + g.fineAmount)
      const sorted = [...g.accounts].sort((a, b) => {
        const diff = (STATUS_ORDER[String(a?.status || '').toLowerCase()] ?? 99) - (STATUS_ORDER[String(b?.status || '').toLowerCase()] ?? 99)
        return diff !== 0 ? diff : Number(b?.outstanding || 0) - Number(a?.outstanding || 0)
      })
      return {
        ...g,
        accounts: sorted,
        netTotal,
        paidPct: netTotal > 0 ? Math.min(100, Math.round((g.paidAmount / netTotal) * 100)) : 0,
        status: getCombinedStatus(sorted),
        overdueCount: sorted.filter(
          (a) => String(a?.status || '').toLowerCase() === 'overdue' && Number(a?.outstanding || 0) > 0,
        ).length,
      }
    })
    .sort((a, b) => {
      const diff = (STATUS_ORDER[a.status] ?? 99) - (STATUS_ORDER[b.status] ?? 99)
      if (diff !== 0) return diff
      if (b.outstanding !== a.outstanding) return b.outstanding - a.outstanding
      return String(a.studentName).localeCompare(String(b.studentName), 'en', { sensitivity: 'base' })
    })
}

export default function FeeAccountsPage() {
  const navigate = useNavigate()
  const [accounts, setAccounts] = useState([])
  const [classes, setClasses] = useState([])
  const [structures, setStructures] = useState([])
  const [loading, setLoading] = useState(true)
  const [filterClass, setFilterClass] = useState('')
  const [filterStatus, setFilterStatus] = useState('')
  const [filterYear, setFilterYear] = useState('')
  const [search, setSearch] = useState('')
  const [expandedGroupId, setExpandedGroupId] = useState(null)
  const [generateModal, setGenerateModal] = useState(false)
  const [genForm, setGenForm] = useState({ classId: '', structureId: '', academicYear: '' })
  const [generating, setGenerating] = useState(false)
  const [exportModal, setExportModal] = useState(false)
  const [exportStep, setExportStep] = useState(1)
  const [exportScope, setExportScope] = useState('current_filters')
  const [exportFormat, setExportFormat] = useState('csv')
  const [exporting, setExporting] = useState(false)

  const fetchData = async () => {
    try {
      setLoading(true)
      const params = {}
      if (filterClass) params.classId = filterClass
      if (filterStatus) params.status = filterStatus
      if (filterYear) params.academicYear = filterYear

      const [accountsRes, classesRes, structuresRes] = await Promise.all([
        feesApi.getAccounts(params),
        classesApi.getAll(),
        feesApi.getStructures(),
      ])

      setAccounts(Array.isArray(accountsRes) ? accountsRes : [])
      setClasses(Array.isArray(classesRes) ? classesRes : [])
      setStructures(Array.isArray(structuresRes) ? structuresRes : [])
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load fee accounts')
      setAccounts([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [filterClass, filterStatus, filterYear])

  const openExportModal = () => {
    setExportStep(1)
    setExportScope('current_filters')
    setExportFormat('csv')
    setExportModal(true)
  }

  const handleExport = async () => {
    try {
      setExporting(true)
      const params = { scope: exportScope, format: exportFormat }
      if (exportScope === 'current_filters') {
        if (filterClass) params.classId = filterClass
        if (filterStatus) params.status = filterStatus
        if (filterYear) params.academicYear = filterYear
      }

      const response = await feesApi.exportAccounts(params, exportFormat)

      if (exportFormat === 'pdf') {
        const { downloadAsPdf } = await import('../utils/downloadPdf.js')
        await downloadAsPdf(response.data, 'fee-accounts', { orientation: 'landscape' })
        toast.success('PDF downloaded')
      } else {
        const blob = new Blob([response.data], { type: 'text/csv' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = 'fee-accounts.csv'
        a.click()
        URL.revokeObjectURL(url)
        toast.success('CSV downloaded')
      }

      setExportModal(false)
    } catch {
      toast.error('Export failed — try again')
    } finally {
      setExporting(false)
    }
  }

  const filteredGroups = useMemo(() => {
    const groups = groupByStudent(accounts)
    if (!search.trim()) return groups
    const q = search.trim().toLowerCase()
    return groups.filter((g) => {
      const nameMatch = g.studentName.toLowerCase().includes(q)
      const admMatch = g.admissionNo.toLowerCase().includes(q)
      const clsMatch = classLabel(g.classItem).toLowerCase().includes(q)
      const planMatch = g.accounts.some((a) => String(a?.structureId?.name || '').toLowerCase().includes(q))
      return nameMatch || admMatch || clsMatch || planMatch
    })
  }, [accounts, search])

  const toggleGroup = (groupId) => {
    setExpandedGroupId((prev) => (prev === groupId ? null : groupId))
  }

  const handleGenerate = async () => {
    const selectedStructure = structures.find((item) => (item._id || item.id) === genForm.structureId)
    const assignedClassIds = (selectedStructure?.classIds || []).map((item) => getClassId(item)).filter(Boolean)
    const targetClassIds = assignedClassIds.length ? assignedClassIds : classes.map((item) => getClassId(item)).filter(Boolean)

    if (!genForm.structureId || !genForm.academicYear.trim()) {
      toast.error('Choose a structure and academic year first')
      return
    }
    if (!isAcademicYear(genForm.academicYear)) {
      toast.error('Use academic year format YYYY-YYYY')
      return
    }
    if (!genForm.classId && targetClassIds.length === 0) {
      toast.error('No class sections available for account generation')
      return
    }

    try {
      setGenerating(true)
      await feesApi.generateAccounts({
        structureId: genForm.structureId,
        academicYear: genForm.academicYear.trim(),
        ...(genForm.classId ? { classId: genForm.classId } : { classIds: targetClassIds }),
      })
      toast.success('Fee accounts generated')
      setGenerateModal(false)
      setGenForm({ classId: '', structureId: '', academicYear: '' })
      fetchData()
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to generate fee accounts')
    } finally {
      setGenerating(false)
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Fee Accounts"
        subtitle="Review student fee accounts, drill into details, and jump quickly into collection or adjustments."
        chips={[
          loading ? 'Loading accounts' : `${filteredGroups.length} student${filteredGroups.length !== 1 ? 's' : ''} • ${accounts.length} account${accounts.length !== 1 ? 's' : ''}`,
          filterClass ? (classes.find((item) => (item._id || item.id) === filterClass)?.section ? `${classes.find((item) => (item._id || item.id) === filterClass)?.name} — ${classes.find((item) => (item._id || item.id) === filterClass)?.section}` : 'Section filter applied') : 'All class sections',
          filterStatus ? `${filterStatus} only` : 'All statuses',
        ]}
        actions={
          <>
            <button onClick={openExportModal} className="btn-secondary" title="Export accounts">
              <Download className="w-4 h-4" /> Export
            </button>
            <button onClick={() => navigate('/fees/analytics')} className="btn-secondary">
              <Sparkles className="w-4 h-4" /> Analytics
            </button>
            <button onClick={() => navigate('/fees/discounts')} className="btn-secondary">
              <Percent className="w-4 h-4" /> Adjust Fees
            </button>
            <button onClick={fetchData} className="btn-secondary">
              <RefreshCw className="w-4 h-4" /> Refresh
            </button>
            <button onClick={() => setGenerateModal(true)} className="btn-primary">
              <Users className="w-4 h-4" /> Generate Accounts
            </button>
          </>
        }
      />

      <div className="card p-4 flex flex-wrap gap-3 items-center">
        <div className="flex items-center gap-2 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 flex-1 min-w-[220px]">
          <Search className="w-4 h-4 text-gray-400 flex-shrink-0" />
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search student, admission no, or class section..."
            className="w-full bg-transparent text-sm outline-none"
          />
        </div>
        <select className="input-field w-full sm:w-auto" value={filterClass} onChange={(event) => setFilterClass(event.target.value)}>
          <option value="">All Class Sections</option>
          {classes.map((item) => (
            <option key={item._id || item.id} value={item._id || item.id}>
              {classLabel(item)}
            </option>
          ))}
        </select>
        <select className="input-field w-full sm:w-auto" value={filterStatus} onChange={(event) => setFilterStatus(event.target.value)}>
          <option value="">All Statuses</option>
          {['pending', 'partial', 'paid', 'overdue'].map((status) => (
            <option key={status} value={status}>
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </option>
          ))}
        </select>
        <input
          value={filterYear}
          onChange={(event) => setFilterYear(event.target.value)}
          className="input-field w-full sm:w-36"
          placeholder="Year e.g. 2025-26"
        />
      </div>

      <div className="space-y-4">
        {loading ? (
          [...Array(5)].map((_, index) => (
            <div key={index} className="card p-5 animate-pulse">
              <div className="h-5 w-1/3 rounded bg-gray-100" />
              <div className="h-4 w-2/3 rounded bg-gray-100 mt-3" />
              <div className="h-3 rounded bg-gray-100 mt-4" />
            </div>
          ))
        ) : filteredGroups.length === 0 ? (
          <div className="empty-state card">
            <CreditCard className="w-10 h-10 text-gray-200" />
            <p className="empty-state-title">No fee accounts found</p>
            <p className="empty-state-copy">Generate accounts from a fee structure or adjust the current filters to see more records.</p>
          </div>
        ) : (
          filteredGroups.map((group) => {
            const isExpanded = expandedGroupId === group._groupId
            const multi = group.accounts.length > 1
            const status = group.status

            return (
              <div key={group._groupId} className="card overflow-hidden">
                <div className="p-5">
                  <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
                    {/* Left: student info */}
                    <div className="flex items-start gap-4 min-w-0">
                      <Avatar src={group.student?.avatar} name={group.studentName} size="md" />
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="text-lg font-semibold text-gray-900 truncate">{group.studentName}</p>
                          <span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold capitalize ${statusColors[status] || statusColors.pending}`}>
                            {status}
                          </span>
                          {group.overdueCount > 0 && (
                            <span className="inline-flex rounded-full bg-red-50 border border-red-100 px-2.5 py-1 text-xs font-semibold text-red-600">
                              {group.overdueCount} overdue
                            </span>
                          )}
                        </div>

                        <p className="text-sm text-gray-500 mt-1">
                          {classLabel(group.classItem)}
                          {group.admissionNo ? ` • Adm# ${group.admissionNo}` : ''}
                          {group.academicYear ? ` • ${group.academicYear}` : ''}
                        </p>

                        <p className="text-sm text-gray-400 mt-0.5">
                          {multi
                            ? `${group.accounts.length} fee plans combined`
                            : (group.accounts[0]?.structureId?.name || 'Fee Plan')}
                        </p>

                        {/* Progress bar */}
                        <div className="mt-3 flex items-center gap-3">
                          <div className="h-2 flex-1 rounded-full bg-gray-100 overflow-hidden">
                            <div
                              className={`h-full rounded-full ${status === 'paid' ? 'bg-green-500' : status === 'overdue' ? 'bg-red-500' : status === 'partial' ? 'bg-amber-500' : 'bg-slate-400'}`}
                              style={{ width: `${group.paidPct}%` }}
                            />
                          </div>
                          <span className="text-xs font-semibold text-gray-500 flex-shrink-0">{group.paidPct}% paid</span>
                        </div>

                        {/* Adjustment badges */}
                        {(group.discountAmount > 0 || group.concessionAmount > 0 || group.fineAmount > 0) ? (
                          <div className="mt-3 flex flex-wrap gap-2">
                            {group.discountAmount > 0 && (
                              <span className="rounded-full bg-green-50 border border-green-100 px-2.5 py-1 text-xs font-semibold text-green-700">
                                Discount {formatCurrency(group.discountAmount)}
                              </span>
                            )}
                            {group.concessionAmount > 0 && (
                              <span className="rounded-full bg-blue-50 border border-blue-100 px-2.5 py-1 text-xs font-semibold text-blue-700">
                                Concession {formatCurrency(group.concessionAmount)}
                              </span>
                            )}
                            {group.fineAmount > 0 && (
                              <span className="rounded-full bg-red-50 border border-red-100 px-2.5 py-1 text-xs font-semibold text-red-600">
                                Fine {formatCurrency(group.fineAmount)}
                              </span>
                            )}
                          </div>
                        ) : null}
                      </div>
                    </div>

                    {/* Right: amounts + actions */}
                    <div className="flex flex-col gap-3 xl:items-end">
                      <div className="text-left xl:text-right">
                        <p className="text-sm text-gray-500">Collected / Total</p>
                        <p className="text-lg font-semibold text-gray-900 mt-1">
                          {formatCurrency(group.paidAmount)}
                          <span className="text-sm font-normal text-gray-400"> / {formatCurrency(group.totalAmount)}</span>
                        </p>
                        <p className="text-sm font-semibold text-red-600 mt-1">
                          Outstanding {formatCurrency(group.outstanding > 0 ? group.outstanding : 0)}
                        </p>
                      </div>

                      <button
                        onClick={() => toggleGroup(group._groupId)}
                        className="btn-secondary min-h-[36px] px-4 py-1.5 text-xs flex items-center gap-1.5"
                      >
                        {isExpanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                        {isExpanded ? 'Hide Fee Plans' : 'View Fee Plans'}
                        <span className="rounded-full bg-gray-200 px-1.5 py-0.5 text-[10px] font-bold text-gray-600">{group.accounts.length}</span>
                      </button>
                    </div>
                  </div>
                </div>

                {/* Expanded: individual fee plan rows */}
                {isExpanded && (
                  <div className="border-t border-gray-100 bg-gray-50 px-5 py-4 space-y-3">
                    {group.accounts.map((account, idx) => {
                      const accountId = account._id || account.id
                      const acctStatus = (account.status || 'pending').toLowerCase()
                      const acctDue = Number(account.outstanding ?? Math.max(0, (account.totalAmount || 0) - (account.paidAmount || 0)))
                      const acctNetTotal = Math.max(
                        0,
                        Number(account.totalAmount || 0) - Number(account.discountAmount || 0) - Number(account.concessionAmount || 0) + Number(account.fineAmount || 0),
                      )
                      const acctPct = acctNetTotal > 0 ? Math.min(100, Math.round((Number(account.paidAmount || 0) / acctNetTotal) * 100)) : 0

                      return (
                        <div key={accountId || idx} className="rounded-2xl border border-gray-200 bg-white p-4">
                          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                            <div className="min-w-0">
                              <div className="flex flex-wrap items-center gap-2">
                                <p className="font-semibold text-gray-900 text-sm">{account.structureId?.name || `Fee Plan ${idx + 1}`}</p>
                                <span className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-semibold capitalize ${statusColors[acctStatus] || statusColors.pending}`}>
                                  {acctStatus}
                                </span>
                              </div>
                              <p className="text-xs text-gray-500 mt-0.5">
                                {classLabel(account.classId)}
                                {account.academicYear ? ` • ${account.academicYear}` : ''}
                              </p>
                              <div className="mt-2 flex items-center gap-2">
                                <div className="h-1.5 w-24 rounded-full bg-gray-100 overflow-hidden">
                                  <div
                                    className={`h-full rounded-full ${acctStatus === 'paid' ? 'bg-green-500' : acctStatus === 'overdue' ? 'bg-red-500' : acctStatus === 'partial' ? 'bg-amber-500' : 'bg-slate-400'}`}
                                    style={{ width: `${acctPct}%` }}
                                  />
                                </div>
                                <span className="text-xs text-gray-400">{acctPct}%</span>
                              </div>
                            </div>

                            <div className="flex flex-col gap-2 sm:items-end">
                              <div className="text-left sm:text-right">
                                <p className="text-sm font-semibold text-gray-900">
                                  {formatCurrency(account.paidAmount || 0)}
                                  <span className="text-xs font-normal text-gray-400"> / {formatCurrency(account.totalAmount || 0)}</span>
                                </p>
                                {acctDue > 0 && (
                                  <p className="text-xs font-semibold text-red-600 mt-0.5">Due {formatCurrency(acctDue)}</p>
                                )}
                              </div>
                              <div className="flex flex-wrap gap-2">
                                <button onClick={() => navigate(`/fees/accounts/${accountId}`)} className="btn-secondary min-h-[32px] px-3 py-1 text-xs">
                                  Details
                                </button>
                                {acctDue > 0 && (
                                  <button onClick={() => navigate(`/fees/collection?accountId=${accountId}`)} className="btn-primary min-h-[32px] px-3 py-1 text-xs">
                                    Collect
                                  </button>
                                )}
                                <button onClick={() => navigate(`/fees/discounts?accountId=${accountId}`)} className="btn-secondary min-h-[32px] px-3 py-1 text-xs">
                                  Adjust
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            )
          })
        )}
      </div>

      {exportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-sm rounded-3xl bg-white shadow-2xl">
            <div className="flex items-start justify-between gap-4 border-b border-gray-100 px-6 py-5">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Export Fee Accounts</h3>
                <p className="text-sm text-gray-500 mt-1">
                  {exportStep === 1 ? 'Step 1 of 2 — Choose which records to export' : 'Step 2 of 2 — Choose export format'}
                </p>
              </div>
              <button onClick={() => setExportModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            {exportStep === 1 ? (
              <div className="px-6 py-5 space-y-3">
                {[
                  { value: 'current_filters', label: 'Current Filters', desc: filterClass || filterStatus || filterYear ? 'Export only the records matching your active filters' : 'No filters applied — exports all records' },
                  { value: 'all', label: 'All Records', desc: 'Export every fee account regardless of filters' },
                ].map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => setExportScope(opt.value)}
                    className={`w-full flex items-start gap-3 rounded-2xl border px-4 py-3 text-left transition-colors ${
                      exportScope === opt.value ? 'border-primary-600 bg-primary-50' : 'border-gray-200 hover:border-primary-200'
                    }`}
                  >
                    <div className={`mt-0.5 h-4 w-4 flex-shrink-0 rounded-full border-2 flex items-center justify-center ${exportScope === opt.value ? 'border-primary-600' : 'border-gray-300'}`}>
                      {exportScope === opt.value && <div className="h-2 w-2 rounded-full bg-primary-600" />}
                    </div>
                    <div>
                      <p className={`font-semibold text-sm ${exportScope === opt.value ? 'text-primary-700' : 'text-gray-800'}`}>{opt.label}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{opt.desc}</p>
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="px-6 py-5 space-y-3">
                {[
                  { value: 'csv', label: 'CSV Spreadsheet', desc: 'Open in Excel or Google Sheets', Icon: FileText },
                  { value: 'pdf', label: 'PDF Document', desc: 'Print-ready formatted report', Icon: Download },
                ].map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => setExportFormat(opt.value)}
                    className={`w-full flex items-center gap-3 rounded-2xl border px-4 py-3 text-left transition-colors ${
                      exportFormat === opt.value ? 'border-primary-600 bg-primary-50' : 'border-gray-200 hover:border-primary-200'
                    }`}
                  >
                    <opt.Icon className={`w-5 h-5 flex-shrink-0 ${exportFormat === opt.value ? 'text-primary-600' : 'text-gray-400'}`} />
                    <div>
                      <p className={`font-semibold text-sm ${exportFormat === opt.value ? 'text-primary-700' : 'text-gray-800'}`}>{opt.label}</p>
                      <p className="text-xs text-gray-500">{opt.desc}</p>
                    </div>
                  </button>
                ))}
              </div>
            )}

            <div className="flex gap-3 justify-end border-t border-gray-100 px-6 py-5">
              {exportStep === 1 ? (
                <>
                  <button onClick={() => setExportModal(false)} className="btn-secondary">Cancel</button>
                  <button onClick={() => setExportStep(2)} className="btn-primary">
                    Next
                  </button>
                </>
              ) : (
                <>
                  <button onClick={() => setExportStep(1)} className="btn-secondary">Back</button>
                  <button onClick={handleExport} disabled={exporting} className="btn-primary">
                    {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                    {exportFormat === 'pdf' ? 'Download PDF' : 'Download CSV'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {generateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-3xl bg-white shadow-2xl">
            <div className="flex items-start justify-between gap-4 border-b border-gray-100 px-6 py-5">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Generate Fee Accounts</h3>
                <p className="text-sm text-gray-500 mt-1">Use the structure’s applicable class sections, or target one section directly.</p>
              </div>
              <button onClick={() => setGenerateModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="px-6 py-5 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Academic Year *</label>
                <input
                  className="input-field w-full"
                  value={genForm.academicYear}
                  onChange={(event) => setGenForm((prev) => ({ ...prev, academicYear: event.target.value }))}
                  placeholder="2025-26"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Fee Structure *</label>
                <select
                  className="input-field w-full"
                  value={genForm.structureId}
                  onChange={(event) => setGenForm((prev) => ({ ...prev, structureId: event.target.value }))}
                >
                  <option value="">Select structure</option>
                  {structures.map((item) => (
                    <option key={item._id || item.id} value={item._id || item.id}>
                      {item.name} ({item.academicYear || 'No year'})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Class Section Filter</label>
                <select
                  className="input-field w-full"
                  value={genForm.classId}
                  onChange={(event) => setGenForm((prev) => ({ ...prev, classId: event.target.value }))}
                >
                  <option value="">All applicable sections</option>
                  {classes.map((item) => (
                    <option key={item._id || item.id} value={item._id || item.id}>
                      {classLabel(item)}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex gap-3 justify-end border-t border-gray-100 px-6 py-5">
              <button onClick={() => setGenerateModal(false)} className="btn-secondary">Cancel</button>
              <button onClick={handleGenerate} disabled={generating} className="btn-primary">
                {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Users className="w-4 h-4" />}
                Generate
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
